import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Stepper from "../components/Stepper";
import * as z from "zod";

// Hero cover images
import heroOnboardLight from "../assets/images/onboard-hero-light-pexels.jpeg";
import heroOnboardDark from "../assets/images/onboard-hero-dark-pexels.jpeg";

/* ------------------------------------------------------------
 * Types & Helpers
 * ----------------------------------------------------------*/

interface OnboardingProgress {
  hasAccount: boolean | null;
  inviteCode: string;
  personalEmail: string;
  institutionalEmails: string[]; // verified emails only (UI-only)
  institutions: string[]; // domains inferred from emails
  preferredName: string;
  displayName: string;
  plan: "free" | "academic" | "institution" | "";
}

const initialState: OnboardingProgress = {
  hasAccount: null,
  inviteCode: "",
  personalEmail: "",
  institutionalEmails: [],
  institutions: [],
  preferredName: "",
  displayName: "",
  plan: "",
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

/* ------------------------------------------------------------
 * Step 0 – Existing account / invite code
 * ----------------------------------------------------------*/

const step0Schema = z.object({
  hasAccount: z.enum(["yes", "no"]),
  inviteCode: z.string().optional(),
});

type Step0Fields = z.infer<typeof step0Schema>;

function Step0({
  defaultValues,
  onNext,
}: {
  defaultValues: Partial<Step0Fields>;
  onNext: (v: { hasAccount: boolean; inviteCode: string }) => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Step0Fields>({
    resolver: zodResolver(step0Schema),
    defaultValues: defaultValues as Step0Fields,
  });

  const hasAccountValue = watch("hasAccount");

  const submit = (data: Step0Fields) => {
    onNext({
      hasAccount: data.hasAccount === "yes",
      inviteCode: data.inviteCode?.trim() || "",
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Welcome! Let’s set up your educator account
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          If your institution sent you an invite link, the invite code may be
          pre-filled.
        </p>
      </div>

      {/* Do you have an account? */}
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          Do you already have an ItsAcademic account?
        </legend>
        <div className="flex items-center gap-6">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="radio"
              value="yes"
              className="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
              {...register("hasAccount", { required: true })}
            />
            <span>Yes</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="radio"
              value="no"
              className="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
              {...register("hasAccount", { required: true })}
            />
            <span>No</span>
          </label>
        </div>
        {errors.hasAccount && (
          <p className="mt-1 text-xs text-red-600">Please choose an option.</p>
        )}
      </fieldset>

      {/* Invite code */}
      <div className="max-w-xs">
        <label htmlFor="inviteCode" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Invite code (optional)
        </label>
        <input
          id="inviteCode"
          type="text"
          placeholder="e.g. ABCD-EFGH"
          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:bg-gray-800 dark:border-gray-700"
          {...register("inviteCode")}
        />
        {errors.inviteCode && <p className="mt-1 text-xs text-red-600">{errors.inviteCode.message}</p>}
      </div>

      <div className="flex items-center justify-between pt-4">
        <div />
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white shadow hover:bg-primary-dark focus:outline-none"
        >
          Continue
        </button>
      </div>

      {hasAccountValue === "yes" && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          You can finish onboarding later — for now we’ll bring you to sign-in
          after confirming your email in the next step.
        </p>
      )}
    </form>
  );
}

/* ------------------------------------------------------------
 * Step 1 – Personal email
 * ----------------------------------------------------------*/

const personalEmailSchema = z.object({
  personalEmail: z.string().email("Enter a valid email address."),
});

type Step1Fields = z.infer<typeof personalEmailSchema>;

function stripSubAddress(email: string) {
  const [local, domain] = email.split("@");
  const rootLocal = local.split("+")[0];
  return `${rootLocal}@${domain}`;
}

function Step1({
  defaultEmail,
  onNext,
  onBack,
}: {
  defaultEmail: string;
  onNext: (email: string) => void;
  onBack: () => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Step1Fields>({
    resolver: zodResolver(personalEmailSchema),
    defaultValues: { personalEmail: defaultEmail },
  });

  const emailValue = watch("personalEmail");

  const submit = (data: Step1Fields) => {
    onNext(data.personalEmail.trim().toLowerCase());
  };

  const showsSubaddressNote = emailValue.includes("+");

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Use your personal email address
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          This keeps your account portable if you move institutions. You can
          manage notification settings and add work emails later.
        </p>
      </div>

      <div className="max-w-sm">
        <label htmlFor="personalEmail" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Personal email address
        </label>
        <input
          id="personalEmail"
          type="email"
          autoComplete="email"
          className={[
            "block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:bg-gray-800 dark:border-gray-700",
            errors.personalEmail ? "border-red-500" : "border-gray-300 dark:border-gray-700",
          ].join(" ")}
          {...register("personalEmail")}
        />
        {errors.personalEmail && (
          <p className="mt-1 text-xs text-red-600">{errors.personalEmail.message}</p>
        )}
        {showsSubaddressNote && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            We treat sub-addresses as part of the same root account: <span className="font-medium">{stripSubAddress(emailValue)}</span>
          </p>
        )}
      </div>

      <div className="flex items-center justify-between pt-4">
        <button
          type="button"
          className="rounded-md px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white shadow hover:bg-primary-dark focus:outline-none"
        >
          Continue
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------
 * Wizard Shell
 * ----------------------------------------------------------*/

const steps = [
  { id: 0, title: "Account & Invite" },
  { id: 1, title: "Personal Email" },
  { id: 2, title: "Institution Emails" },
  { id: 3, title: "Institutions" },
  { id: 4, title: "Profile" },
  { id: 5, title: "Plan" },
  { id: 6, title: "Done" },
];

export default function EducatorOnboardingPage() {
  const query = useQuery();
  const navigate = useNavigate();

  const [state, setState] = useState<OnboardingProgress>(() => {
    const emailFromQs = query.get("email") ?? "";
    const inviteFromQs = query.get("invite") ?? "";
    return {
      ...initialState,
      personalEmail: emailFromQs,
      inviteCode: inviteFromQs,
    };
  });

  const [currentStep, setCurrentStep] = useState(0);

  const goNext = () => setCurrentStep((s) => s + 1);
  const goPrev = () => setCurrentStep((s) => s - 1);

  // When finished minimal flow, redirect accordingly (placeholder logic)
  useEffect(() => {
    if (currentStep >= steps.length) {
      if (state.hasAccount) {
        navigate("/login", { replace: true });
      } else {
        navigate("/signup", { replace: true });
      }
    }
  }, [currentStep, state.hasAccount, navigate]);

  /* ---------------------------------------------------------- */
  /* Render                                                       */
  /* ---------------------------------------------------------- */

  /* ---------------------- Step 2 component ------------------ */

  function Step2InstitutionEmails({
    verifiedEmails,
    onNext,
    onBack,
  }: {
    verifiedEmails: string[];
    onNext: (emails: string[]) => void;
    onBack: () => void;
  }) {
    interface Item {
      email: string;
      code: string; // user input
      sentCode: string; // simulated sent code
      verified: boolean;
    }

    const [items, setItems] = useState<Item[]>(
      verifiedEmails.map((e) => ({ email: e, code: "", sentCode: "", verified: true }))
    );

    const [newEmail, setNewEmail] = useState("");
    const [sending, setSending] = useState(false);

    const addEmail = async () => {
      if (!newEmail) return;
      const email = newEmail.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
      if (items.some((i) => i.email === email)) {
        setNewEmail("");
        return;
      }
      setSending(true);
      // Simulate API sending code
      await new Promise((r) => setTimeout(r, 600));
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      setItems((prev) => [...prev, { email, code: "", sentCode: code, verified: false }]);
      setNewEmail("");
      setSending(false);
    };

    const verifyCode = (index: number) => {
      setItems((prev) => {
        const copy = [...prev];
        copy[index].verified = copy[index].code.toUpperCase() === copy[index].sentCode;
        return copy;
      });
    };

    const canContinue = items.length === 0 || items.every((i) => i.verified);

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Add your institutional email address(es)
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Verified work e-mails let us connect you to the right department and
            unlock institution-wide benefits. You can skip this step and add
            them later in settings.
          </p>
        </div>

        {/* Add email */}
        <div className="flex max-w-md gap-2">
          <input
            type="email"
            placeholder="name@college.edu"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:bg-gray-800 dark:border-gray-700"
          />
          <button
            type="button"
            onClick={addEmail}
            disabled={sending || !newEmail}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow hover:bg-primary-dark disabled:opacity-60"
          >
            {sending ? "Sending…" : "Add"}
          </button>
        </div>

        {/* List */}
        {items.length > 0 && (
          <ul className="space-y-4">
            {items.map((item, idx) => (
              <li key={item.email} className="rounded-md border border-gray-200 p-4 dark:border-gray-700">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-medium">{item.email}</span>
                  {item.verified ? (
                    <span className="text-sm text-green-600">Verified</span>
                  ) : (
                    <span className="text-sm text-yellow-600">Code sent</span>
                  )}
                </div>
                {!item.verified && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      maxLength={6}
                      value={item.code}
                      onChange={(e) =>
                        setItems((prev) => {
                          const copy = [...prev];
                          copy[idx].code = e.target.value.toUpperCase();
                          return copy;
                        })
                      }
                      className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm uppercase tracking-wider focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:bg-gray-800 dark:border-gray-700"
                    />
                    <button
                      type="button"
                      onClick={() => verifyCode(idx)}
                      className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-primary-dark"
                    >
                      Verify
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Nav buttons */}
        <div className="flex items-center justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="rounded-md px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            Back
          </button>
          <button
            type="button"
            disabled={!canContinue}
            onClick={() => onNext(items.filter((i) => i.verified).map((i) => i.email))}
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white shadow hover:bg-primary-dark disabled:opacity-60"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  /* --------------------- Step 3 component ------------------- */

  function Step3Institutions({
    institutions,
    onNext,
    onBack,
  }: {
    institutions: string[];
    onNext: () => void;
    onBack: () => void;
  }) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Confirm your institution(s)
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Based on your verified email domains we think you’re associated with:
          </p>
        </div>

        <ul className="space-y-3">
          {institutions.map((inst) => (
            <li key={inst} className="rounded-md border border-gray-200 px-4 py-3 dark:border-gray-700">
              {inst}
            </li>
          ))}
        </ul>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          Don’t see the right institution? You can add more institutional emails
          later in Settings.
        </p>

        <div className="flex items-center justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="rounded-md px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onNext}
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white shadow hover:bg-primary-dark"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  /* --------------------- Step 4 component ------------------- */

  const profileSchema = z.object({
    preferredName: z.string().min(2, "Enter at least 2 characters."),
    displayName: z.string().min(2, "Enter at least 2 characters."),
  });
  type ProfileFields = z.infer<typeof profileSchema>;

  function Step4Profile({
    initial,
    onNext,
    onBack,
  }: {
    initial: { preferredName: string; displayName: string };
    onNext: (v: { preferredName: string; displayName: string }) => void;
    onBack: () => void;
  }) {
    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm<ProfileFields>({
      resolver: zodResolver(profileSchema),
      defaultValues: initial,
    });

    const submit = (data: ProfileFields) => onNext(data);

    return (
      <form onSubmit={handleSubmit(submit)} className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Tell us about yourself
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            These details help personalise the experience for your students.
            You can change them later.
          </p>
        </div>

        <div className="max-w-sm space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="preferredName">
              Preferred name
            </label>
            <input
              id="preferredName"
              type="text"
              {...register("preferredName")}
              className={[
                "block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:bg-gray-800 dark:border-gray-700",
                errors.preferredName ? "border-red-500" : "border-gray-300 dark:border-gray-700",
              ].join(" ")}
            />
            {errors.preferredName && (
              <p className="mt-1 text-xs text-red-600">{errors.preferredName.message}</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="displayName">
              Display name (visible to students)
            </label>
            <input
              id="displayName"
              type="text"
              {...register("displayName")}
              className={[
                "block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:bg-gray-800 dark:border-gray-700",
                errors.displayName ? "border-red-500" : "border-gray-300 dark:border-gray-700",
              ].join(" ")}
            />
            {errors.displayName && (
              <p className="mt-1 text-xs text-red-600">{errors.displayName.message}</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="rounded-md px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            Back
          </button>
          <button
            type="submit"
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white shadow hover:bg-primary-dark"
          >
            Continue
          </button>
        </div>
      </form>
    );
  }

  /* --------------------- Step 5 component ------------------- */

  function Step5Plan({ inviteCodePresent, onNext, onBack }: { inviteCodePresent: boolean; onNext: (plan: string) => void; onBack: () => void }) {
    const [plan, setPlan] = useState(inviteCodePresent ? "institution" : "free");

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Choose your plan</h2>
        </div>

        <div className="space-y-4">
          {inviteCodePresent && (
            <label className="block cursor-pointer rounded-md border p-4 shadow-sm hover:border-primary dark:border-gray-700">
              <input
                type="radio"
                className="mr-2"
                value="institution"
                checked={plan === "institution"}
                onChange={() => setPlan("institution")}
              />
              Covered by your institution (via invite code)
            </label>
          )}

          <label className="block cursor-pointer rounded-md border p-4 shadow-sm hover:border-primary dark:border-gray-700">
            <input
              type="radio"
              className="mr-2"
              value="free"
              checked={plan === "free"}
              onChange={() => setPlan("free")}
            />
            Free educator plan
          </label>

          <label className="block cursor-pointer rounded-md border p-4 shadow-sm hover:border-primary dark:border-gray-700">
            <input
              type="radio"
              className="mr-2"
              value="academic"
              checked={plan === "academic"}
              onChange={() => setPlan("academic")}
            />
            Academic Pro (paid) – more storage & advanced analytics
          </label>
        </div>

        <div className="flex items-center justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="rounded-md px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => onNext(plan)}
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white shadow hover:bg-primary-dark"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  /* --------------------- Step 6 component ------------------- */

  function Step6Done() {
    return (
      <div className="flex flex-col items-center justify-center space-y-6 py-12 text-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          Setting up your account…
        </h2>
        <p className="max-w-md text-sm text-gray-600 dark:text-gray-400">
          You can close this window; we’ll email you once everything is ready.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-6">
      {/* Hero cover */}
      <section className="relative overflow-hidden rounded-2xl">
        <img
          src={heroOnboardLight}
          alt="Educator onboarding visual"
          className="theme-light-only absolute inset-0 h-40 w-full object-cover sm:h-52"
        />
        <img
          src={heroOnboardDark}
          alt="Educator onboarding visual"
          className="theme-dark-only absolute inset-0 h-40 w-full object-cover sm:h-52"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white dark:from-gray-900/70 dark:via-gray-900/40 dark:to-gray-900" />
        <NavLink
          to="/"
          className="relative z-10 flex h-16 items-center justify-center text-lg font-semibold text-primary dark:text-primary"
        >
          ItsAcademic.ai
        </NavLink>

        {/* Photo credits */}
        <p className="absolute bottom-1 right-2 text-[10px] text-gray-500 dark:text-gray-400">
          Photos by{' '}
          <a
            href="https://www.pexels.com/@max-fischer/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Max Fischer
          </a>{' '}
          &{' '}
          <a
            href="https://www.pexels.com/@pavel-danilyuk/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Pavel Danilyuk
          </a>{' '}
          on <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer" className="underline">Pexels</a>
        </p>
      </section>

      {/* Stepper */}
      <Stepper steps={steps.map((s) => s.title)} currentStep={currentStep} />

      {/* Card with step content */}
      <div className="rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800 dark:text-gray-100">
          {currentStep === 0 && (
            <Step0
              defaultValues={{
                hasAccount: state.hasAccount === null ? undefined : state.hasAccount ? "yes" : "no",
                inviteCode: state.inviteCode,
              }}
              onNext={(values) => {
                setState((prev) => ({ ...prev, ...values }));
                goNext();
              }}
            />
          )}

          {currentStep === 1 && (
            <Step1
              defaultEmail={state.personalEmail}
              onNext={(email) => {
                setState((prev) => ({ ...prev, personalEmail: email }));
                goNext();
              }}
              onBack={goPrev}
            />
          )}

          {currentStep === 2 && (
            <Step2InstitutionEmails
              verifiedEmails={state.institutionalEmails}
              onNext={(emails) => {
                // derive institutions from domains
                const domains = emails.map((e) => e.split("@")[1]);
                const uniqueDomains = Array.from(new Set(domains));
                setState((prev) => ({
                  ...prev,
                  institutionalEmails: emails,
                  institutions: uniqueDomains,
                }));
                goNext();
              }}
              onBack={goPrev}
            />
          )}

          {currentStep === 3 && (
            <Step3Institutions
              institutions={state.institutions}
              onNext={goNext}
              onBack={goPrev}
            />
          )}

          {currentStep === 4 && (
            <Step4Profile
              initial={{ preferredName: state.preferredName, displayName: state.displayName }}
              onNext={(vals) => {
                setState((prev) => ({ ...prev, ...vals }));
                goNext();
              }}
              onBack={goPrev}
            />
          )}

          {currentStep === 5 && (
            <Step5Plan
              inviteCodePresent={!!state.inviteCode}
              onNext={(plan) => {
                setState((prev) => ({ ...prev, plan: plan as any }));
                goNext();
              }}
              onBack={goPrev}
            />
          )}

          {currentStep === 6 && <Step6Done />}
        </div>
    </div>
  );
}
